import React from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, MessageCircle } from 'lucide-react';

const ContactPage = () => {

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone Consultation',
      details: ['+65 8861 3196', 'Monday to Sunday 8:00-22:00'],
      color: 'bg-orange-600',
      href: 'tel:+6588613196'
    },
    {
      icon: MessageCircle,
      title: 'WhatsApp Chat',
      details: ['+65 8861 3196', 'Instant responses 24/7'],
      color: 'bg-green-600',
      href: 'https://wa.me/6588613196',
      external: true
    },
    {
      icon: Mail,
      title: 'Email Consultation',
      details: ['sales@swiftfixpro.com', 'Response within 24 hours'],
      color: 'bg-orange-600',
      href: 'mailto:sales@swiftfixpro.com'
    },
    {
      icon: MapPin,
      title: 'Company Address',
      details: ['Blk 625 Aljunied Road #05-08', 'Aljunied Industrial Complex Singapore 389836'],
      color: 'bg-orange-600',
      href: 'https://maps.google.com/?q=Blk 625 Aljunied Road Singapore 389836'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Modern Hero Section - Matching HomePage Style */}
      <section className="relative overflow-hidden">
        {/* Background with geometric patterns */}
        <div className="absolute inset-0 bg-orange-600">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700"></div>
          {/* Geometric shapes */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500 rounded-full opacity-20 transform translate-x-32 -translate-y-32"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-orange-700 rounded-full opacity-30 transform -translate-x-24 translate-y-24"></div>
          <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-white opacity-5 rounded-full"></div>
          <div className="absolute top-1/4 right-1/3 w-48 h-48 bg-orange-400 rounded-full opacity-10 transform rotate-45"></div>
        </div>
        
        <div className="relative container mx-auto px-4 py-24">
          <div className="max-w-4xl mx-auto text-center text-white">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="mb-6"
            >
              <span className="inline-block px-4 py-2 bg-orange-500 bg-opacity-30 rounded-full text-orange-100 text-sm font-medium mb-4 backdrop-blur-sm">
                Get In Touch
              </span>
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="text-4xl md:text-6xl font-bold mb-6 leading-tight"
            >
              Contact
              <span className="block text-orange-200">SwiftFixPro</span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="text-xl text-orange-100 max-w-3xl mx-auto mb-10 leading-relaxed"
            >
              We are always ready to help you. If you have any questions or need consultation, please feel free to contact us anytime.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <a
                href="tel:+6588613196"
                className="bg-white text-orange-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-lg inline-flex items-center"
              >
                <Phone className="mr-2 w-5 h-5" />
                Call Now
              </a>
              <a
                href="https://wa.me/6588613196"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-green-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-green-700 transition-colors text-lg inline-flex items-center shadow-lg hover:shadow-xl"
              >
                <MessageCircle className="mr-2 w-5 h-5" />
                WhatsApp
              </a>
              <a
                href="mailto:sales@swiftfixpro.com"
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-orange-600 transition-colors text-lg inline-flex items-center"
              >
                <Mail className="mr-2 w-5 h-5" />
                Email Us
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Enhanced Contact Info Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-orange-50/30 relative overflow-hidden">
        {/* Geometric Background Elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-10 w-32 h-32 bg-orange-200 rounded-full opacity-20 animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-40 h-40 bg-orange-100 rounded-full opacity-30"></div>
          <div className="absolute top-1/2 left-1/3 w-6 h-6 bg-orange-500 rotate-45 opacity-30"></div>
          <div className="absolute top-1/3 right-1/4 w-8 h-8 bg-orange-400 rounded-full opacity-20"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-orange-500 bg-opacity-10 rounded-full text-orange-600 text-sm font-medium mb-6">
              Contact Information
            </span>
            <h2 className="text-4xl font-bold mb-6 text-gray-900">Get In Touch With Us</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose your preferred way to contact us. We're here to help you with all your property maintenance needs.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactInfo.map((info, index) => (
              <motion.a
                key={index}
                href={info.href}
                target={info.external || info.icon === MapPin ? "_blank" : undefined}
                rel={info.external || info.icon === MapPin ? "noopener noreferrer" : undefined}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group block"
              >
                <div className={`text-center p-8 rounded-2xl bg-white hover:bg-white hover:shadow-xl transition-all duration-300 h-full border border-gray-100 ${info.color === 'bg-green-600' ? 'group-hover:border-green-200' : 'group-hover:border-orange-200'}`}>
                  <div className={`inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br ${info.color.replace('bg-', 'from-')} ${info.color.replace('bg-', 'to-').replace('600', '700')} text-white rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                    <info.icon size={28} />
                  </div>
                  <h3 className={`text-xl font-semibold mb-4 text-gray-900 ${info.color === 'bg-green-600' ? 'group-hover:text-green-600' : 'group-hover:text-orange-600'} transition-colors duration-300`}>
                    {info.title}
                  </h3>
                  {info.details.map((detail, i) => (
                    <p key={i} className="text-gray-600 mb-2 leading-relaxed">
                      {detail}
                    </p>
                  ))}

                  {/* Decorative bottom accent */}
                  <div className="mt-6 h-1 bg-gradient-to-r from-orange-400 to-orange-600 rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Map and Additional Info Section */}
      <section id="contact-form" className="py-20 bg-white relative overflow-hidden">
        {/* Geometric Background Elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 right-10 w-24 h-24 bg-orange-200 rounded-full opacity-20"></div>
          <div className="absolute bottom-20 left-10 w-32 h-32 bg-orange-100 rounded-full opacity-30"></div>
          <div className="absolute top-1/3 left-1/4 w-4 h-4 bg-orange-500 rotate-45 opacity-40"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          {/* Enhanced Map */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100">
              <div className="mb-6 text-center">
                <span className="inline-block px-3 py-1 bg-orange-100 text-orange-600 rounded-full text-sm font-medium mb-3">
                  Our Location
                </span>
                <h3 className="text-3xl font-bold text-gray-900 mb-2">Visit Our Office</h3>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  Come visit us at our modern office in the heart of Singapore's business district.
                </p>
              </div>

              <div className="rounded-xl overflow-hidden h-96 mb-6">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3988.7598745882556!2d103.88635831533454!3d1.3240644620316636!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31da17649d89c91f%3A0x3e6f6b6d6f5d5e5f!2sAljunied%20Industrial%20Complex!5e0!3m2!1sen!2ssg!4v1234567890123!5m2!1sen!2ssg"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Swift Fix Pro Location"
                ></iframe>
              </div>

              <div className="p-6 bg-gradient-to-br from-orange-50 to-orange-100/50 rounded-xl">
                <div className="flex items-start gap-4 max-w-2xl mx-auto">
                  <MapPin className="w-6 h-6 text-orange-600 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-bold text-gray-900 text-lg">Blk 625 Aljunied Road #05-08</p>
                    <p className="text-gray-700">Aljunied Industrial Complex Singapore 389836</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

        </div>
      </section>
    </div>
  );
};

export default ContactPage; 